import React, { useEffect, useState, useCallback, Fragment } from 'react';
import apiClient from '../api/axios';
import { Container, Row, Col, Card, Spinner, Alert, Button, ListGroup, Pagination, ButtonGroup, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { roleTranslations } from '../utils/translations';
import { formatUtcToLocal } from '../utils/dateUtils';
import { toast } from 'react-toastify';

import ConfirmationModal from '../components/ConfirmationModal';
import { useAuth } from '../contexts/AuthContext';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="text-dark p-2"
      style={{ background: 'none', border: 'none' }}
    >
      {children}
    </button>
));

const AdminUsers = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const usersPerPage = parseInt(process.env.REACT_APP_ADMIN_USERS_PER_PAGE || '20', 10);

    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
    const [selectedOrganizationName, setSelectedOrganizationName] = useState('Все');
    const [selectedOrgOption, setSelectedOrgOption] = useState(null);

    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showConfirmDeleteUserModal, setShowConfirmDeleteUserModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [showConfirmBanUserModal, setShowConfirmBanUserModal] = useState(false);
    const [userToBan, setUserToBan] = useState(null);

    const fetchUsers = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        const skip = (page - 1) * usersPerPage;
        const limit = usersPerPage;

        try {
            let usersUrl = `/admin/users/?skip=${skip}&limit=${limit}`;
            if (selectedOrganizationId !== null && selectedOrganizationId !== undefined) {
                usersUrl += `&organization_id=${selectedOrganizationId}`;
            }
            const usersResponse = await apiClient.get(usersUrl);
            setUsers(usersResponse.data.users);
            setTotalUsersCount(usersResponse.data.total_count);
            setLoading(false);
        } catch (err) {
            setError('Не удалось загрузить данные пользователей.');
            setLoading(false);
            console.error(err);
        }
    }, [usersPerPage, selectedOrganizationId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedOrganizationId]);

    useEffect(() => {
        fetchUsers(currentPage);
    }, [fetchUsers, currentPage]);

    const loadOrganizationOptions = async (inputValue) => {
        if (!inputValue) {
            return [];
        }
        try {
            const response = await apiClient.get(`/admin/organizations/search?q=${inputValue}`);
            return response.data.map(org => ({
                value: org.id,
                label: org.name
            }));
        } catch (error) {
            console.error('Error loading organizations', error);
            return [];
        }
    };

    const handleOrganizationChange = (selectedOption) => {
        setSelectedOrgOption(selectedOption);
        if (selectedOption) {
            setSelectedOrganizationId(selectedOption.value);
            setSelectedOrganizationName(selectedOption.label);
        } else {
            setFilter(null, 'Все', null);
        }
    };

    const setFilter = (id, name, option) => {
        setSelectedOrganizationId(id);
        setSelectedOrganizationName(name);
        setSelectedOrgOption(option);
    };

    const totalPages = Math.ceil(totalUsersCount / usersPerPage);

    const handleDeleteUserClick = (user) => {
        setUserToDelete(user);
        setShowConfirmDeleteUserModal(true);
    };

    const handleConfirmDeleteUser = async () => {
        if (userToDelete) {
            try {
                await apiClient.delete(`/admin/users/${userToDelete.id}`);
                toast.success(`Пользователь ${userToDelete.email} успешно удален!`);
                fetchUsers(currentPage);
            } catch (err) {
                let errorMessage = 'Ошибка при удалении пользователя.';
                if (err.response && err.response.data && err.response.data.detail) {
                    errorMessage = err.response.data.detail;
                }
                toast.error(errorMessage);
            } finally {
                setShowConfirmDeleteUserModal(false);
                setUserToDelete(null);
            }
        }
    };

    const handleCancelDeleteUser = () => {
        setShowConfirmDeleteUserModal(false);
        setUserToDelete(null);
    };

    const handleBanUserClick = (user) => {
        setUserToBan(user);
        setShowConfirmBanUserModal(true);
    };

    const handleConfirmBanUser = async () => {
        if (userToBan) {
            try {
                await apiClient.put(`/admin/users/${userToBan.id}/ban`, { is_banned: true });
                toast.success(`Пользователь ${userToBan.email} успешно забанен!`);
                fetchUsers(currentPage);
            } catch (err) {
                toast.error('Ошибка при бане пользователя.');
            } finally {
                setShowConfirmBanUserModal(false);
                setUserToBan(null);
            }
        }
    };

    const handleCancelBanUser = () => {
        setShowConfirmBanUserModal(false);
        setUserToBan(null);
    };

    const handleUnbanUser = async (user) => {
        try {
            await apiClient.put(`/admin/users/${user.id}/ban`, { is_banned: false });
            toast.success(`Пользователь ${user.email} успешно разбанен!`);
            fetchUsers(currentPage);
        } catch (err) {
            toast.error('Ошибка при разбане пользователя.');
        }
    };

    const handleEditUserClick = (user) => {
        setEditingUser(user);
        setShowEditUserModal(true);
    };

    const handleCloseEditUserModal = () => {
        setShowEditUserModal(false);
        setEditingUser(null);
    };

    const handleUserUpdated = () => {
        fetchUsers(currentPage);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>,
            );
        }
        return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
    };

    if (loading && !users.length) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container className="mt-5">
                <h1 className="mb-4">Управление пользователями</h1>
                <Row>
                    <Col md={12} className="mb-4">
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                                <div className="d-flex align-items-center flex-wrap">
                                    <span className="me-2 mb-2 mb-md-0">
                                        Пользователи {selectedOrganizationName ? `(${selectedOrganizationName})` : ''} ({totalUsersCount})
                                    </span>
                                    <ButtonGroup size="sm" className="me-2 mb-2 mb-md-0">
                                        <Button variant="outline-secondary" onClick={() => setFilter(null, 'Все', null)}>Все</Button>
                                        <Button variant="outline-secondary" onClick={() => setFilter(0, 'Без организации', { value: 0, label: 'Без организации' })}>Без организации</Button>
                                    </ButtonGroup>
                                    <div style={{ minWidth: '250px' }}>
                                        <AsyncSelect
                                            value={selectedOrgOption}
                                            cacheOptions
                                            loadOptions={loadOrganizationOptions}
                                            onChange={handleOrganizationChange}
                                            isClearable
                                            placeholder="Поиск по организации..."
                                        />
                                    </div>
                                </div>
                                <Button variant="success" size="sm" onClick={() => setShowCreateUserModal(true)}>
                                    <i className="fas fa-plus"></i> Создать пользователя
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {users.length > 0 ? (
                                    <ListGroup variant="flush">
                                        {users.map(user => (
                                            <ListGroup.Item key={user.id} className="py-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1 me-2">
                                                        <h5 className="mb-1">{user.name || user.email}</h5>
                                                        {user.name && <p className="text-muted mb-1">{user.email}</p>}
                                                        
                                                        {user.organization && <p className="mb-1">
                                                            <i className="fas fa-building me-1"></i>
                                                            <Link to={`/admin/organizations/${user.organization.id}`} className="btn btn-link p-0" style={{ textTransform: 'none' }}>
                                                                {user.organization.name}
                                                            </Link>
                                                            {user.role_in_organization && ` (${roleTranslations[user.role_in_organization]})`}
                                                        </p>}

                                                        <p className="mb-1">
                                                            <span className="me-2">
                                                                {user.is_email_confirmed ? <i className="fas fa-check-circle text-success me-1" title="Email подтвержден"></i> : <i className="fas fa-times-circle text-danger me-1" title="Email не подтвержден"></i>}
                                                                Email
                                                            </span>
                                                            <span className="me-2">
                                                                {user.consent_to_data_processing ? <i className="fas fa-check-circle text-success me-1" title="Согласие на обработку данных предоставлено"></i> : <i className="fas fa-times-circle text-danger me-1" title="Согласие не предоставлено"></i>}
                                                                Согласие
                                                            </span>
                                                            <span>
                                                                {user.is_banned ? <i className="fas fa-ban text-danger me-1" title="Забанен"></i> : <i className="fas fa-circle text-secondary me-1" title="Не забанен"></i>}
                                                                Бан
                                                            </span>
                                                        </p>
                                                        
                                                        <small className="text-muted">
                                                            Создан: {formatUtcToLocal(user.created_at)} | Изменен: {formatUtcToLocal(user.updated_at)}
                                                            <br />
                                                            Последняя активность: {user.last_active_at ? formatUtcToLocal(user.last_active_at) : 'никогда'}
                                                        </small>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <Dropdown>
                                                            <Dropdown.Toggle as={CustomToggle} id={`dropdown-for-${user.id}`}>
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </Dropdown.Toggle>

                                                            <Dropdown.Menu align="end">
                                                                <Dropdown.Item onClick={() => handleEditUserClick(user)}>
                                                                    <i className="fas fa-edit me-2"></i>Редактировать
                                                                </Dropdown.Item>
                                                                {user.id !== currentUser.id && (
                                                                    <Fragment>
                                                                        {user.is_banned ? (
                                                                            <Dropdown.Item onClick={() => handleUnbanUser(user)}>
                                                                                <i className="fas fa-check-circle me-2"></i>Разбанить
                                                                            </Dropdown.Item>
                                                                        ) : (
                                                                            <Dropdown.Item onClick={() => handleBanUserClick(user)}>
                                                                                <i className="fas fa-ban me-2"></i>Забанить
                                                                            </Dropdown.Item>
                                                                        )}
                                                                        <Dropdown.Divider />
                                                                        <Dropdown.Item onClick={() => handleDeleteUserClick(user)} className="text-danger">
                                                                            <i className="fas fa-trash me-2"></i>Удалить
                                                                        </Dropdown.Item>
                                                                    </Fragment>
                                                                )}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                ) : (
                                    <div className="text-center p-4">Пользователей пока нет.</div>
                                )}
                            </Card.Body>
                            <div className="d-flex justify-content-between align-items-center p-3">
                                <Button variant="secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}>
                                    <i className="fas fa-chevron-left"></i>
                                    <span className="d-none d-md-inline ms-2">Предыдущая</span>
                                </Button>
                                <span>Страница {currentPage} из {Math.max(1, totalPages)}</span>
                                <Button variant="secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading}>
                                    <span className="d-none d-md-inline me-2">Следующая</span>
                                    <i className="fas fa-chevron-right"></i>
                                </Button>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <ConfirmationModal
                show={showConfirmDeleteUserModal}
                title="Подтвердить удаление пользователя"
                message={`Вы уверены, что хотите удалить пользователя ${userToDelete?.email}? Это действие необратимо.`}
                onConfirm={handleConfirmDeleteUser}
                onCancel={handleCancelDeleteUser}
                confirmText="Удалить"
                cancelText="Отмена"
            />

            <ConfirmationModal
                show={showConfirmBanUserModal}
                title="Подтвердить бан пользователя"
                message={`Вы уверены, что хотите забанить пользователя ${userToBan?.email}?`}
                onConfirm={handleConfirmBanUser}
                onCancel={handleCancelBanUser}
                confirmText="Забанить"
                cancelText="Отмена"
            />

            <CreateUserModal
                show={showCreateUserModal}
                handleClose={() => setShowCreateUserModal(false)}
                onUserCreated={() => {
                    setShowCreateUserModal(false);
                    fetchUsers(currentPage);
                }}
            />

            <EditUserModal
                show={showEditUserModal}
                handleClose={handleCloseEditUserModal}
                user={editingUser}
                onUserUpdated={handleUserUpdated}
            />
            {renderPagination()}
        </Fragment>
    );
};

export default AdminUsers;